import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, RefreshCw, Building2, Send } from 'lucide-react';
import {
  getBusinessTypeMapping,
  getBusinessTypeSuggestions,
  joinCommaValues,
} from '../lib/businessTypeTaxonomy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientBusinessLeadsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({ campaigns: [], summary: {} });
  const [feed, setFeed] = useState([]);
  const [campaignId, setCampaignId] = useState('all');
  const [businessTypeInput, setBusinessTypeInput] = useState('');
  const [businessTypeTokens, setBusinessTypeTokens] = useState([]);
  const [businessTypeSuggestions, setBusinessTypeSuggestions] = useState([]);

  const getHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchOverview = useCallback(async () => {
    const res = await axios.get(`${API_URL}/api/client-portal/business-leads/overview`, {
      headers: getHeaders(),
    });
    setOverview(res.data || { campaigns: [], summary: {} });
  }, []);

  const fetchFeed = useCallback(async (selectedCampaignId = campaignId) => {
    const params = { limit: 150 };
    if (selectedCampaignId && selectedCampaignId !== 'all') params.campaign_id = selectedCampaignId;
    const res = await axios.get(`${API_URL}/api/client-portal/business-leads/feed`, {
      headers: getHeaders(),
      params,
    });
    setFeed(res.data.feed || []);
  }, [campaignId]);

  const loadData = useCallback(async (selectedCampaignId = campaignId) => {
    try {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchFeed(selectedCampaignId)]);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('client_token');
        navigate('/client-portal');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load business leads data');
      }
    } finally {
      setLoading(false);
    }
  }, [campaignId, fetchFeed, fetchOverview, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const summary = overview.summary || {};

  const businessTypeCodeSnapshot = useMemo(() => {
    const sic = new Set();
    const naics = new Set();
    businessTypeTokens.forEach((token) => {
      const mapping = getBusinessTypeMapping(token);
      if (!mapping) return;
      (mapping.sic_codes || []).forEach((code) => sic.add(code));
      (mapping.naics_codes || []).forEach((code) => naics.add(code));
    });
    return {
      sic: joinCommaValues(Array.from(sic)),
      naics: joinCommaValues(Array.from(naics)),
    };
  }, [businessTypeTokens]);

  const filteredFeed = useMemo(() => {
    if (!businessTypeTokens.length) return feed;
    const tokenSet = new Set(businessTypeTokens.map((t) => t.toLowerCase()));
    return feed.filter((row) => tokenSet.has(String(row.business_type || '').toLowerCase()));
  }, [feed, businessTypeTokens]);

  const applyClientBusinessTypeToken = (rawValue) => {
    const typed = String(rawValue || '').trim().toLowerCase();
    if (!typed) return;

    const suggestion = getBusinessTypeSuggestions(typed)[0];
    const mapping = getBusinessTypeMapping(typed) || suggestion || null;
    const tokenValue = mapping?.type || typed;

    if (businessTypeTokens.includes(tokenValue)) {
      setBusinessTypeInput('');
      setBusinessTypeSuggestions([]);
      return;
    }

    setBusinessTypeTokens((prev) => [...prev, tokenValue]);
    setBusinessTypeInput('');
    setBusinessTypeSuggestions([]);
  };

  const removeLastClientBusinessToken = () => {
    if (!businessTypeTokens.length) return;
    setBusinessTypeTokens((prev) => prev.slice(0, -1));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="client-business-leads-loading">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-business-leads-page">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="client-business-leads-title">Business Leads Feed</h1>
            <p className="text-sm text-slate-600" data-testid="client-business-leads-subtitle">
              Source type, filter set, lead volume, and cost tracking for your business campaigns.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="client-business-leads-refresh" onClick={() => loadData(campaignId)}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button data-testid="client-business-leads-back-dashboard" onClick={() => navigate('/client-portal/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardDescription>Campaigns</CardDescription></CardHeader>
            <CardContent className="text-2xl font-semibold" data-testid="client-business-summary-campaigns">{summary.campaign_count || 0}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>Delivered Leads</CardDescription></CardHeader>
            <CardContent className="text-2xl font-semibold" data-testid="client-business-summary-delivered">{summary.total_delivered || 0}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>Queued (Drip Pending)</CardDescription></CardHeader>
            <CardContent className="text-2xl font-semibold" data-testid="client-business-summary-queued">{summary.total_queued || 0}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>Total Cost</CardDescription></CardHeader>
            <CardContent className="text-2xl font-semibold" data-testid="client-business-summary-cost">${Number(summary.total_cost || 0).toFixed(2)}</CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Campaign Source Settings</CardTitle>
            <CardDescription>Lead type, source filter, drip setup, and cost per lead.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Lead Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Filters</TableHead>
                    <TableHead>Cost/Lead</TableHead>
                    <TableHead>Drip/Day</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>Queued</TableHead>
                    <TableHead>Subtotal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview.campaigns?.length ? overview.campaigns.map((campaign) => (
                    <TableRow key={campaign.campaign_id} data-testid={`client-business-campaign-${campaign.campaign_id}`}>
                      <TableCell>{campaign.campaign_name}</TableCell>
                      <TableCell><Badge variant="secondary">{campaign.lead_type}</Badge></TableCell>
                      <TableCell>{campaign.source_type || '-'}</TableCell>
                      <TableCell className="max-w-xs truncate" title={JSON.stringify(campaign.filters_snapshot || {})}>
                        {JSON.stringify(campaign.filters_snapshot || {})}
                      </TableCell>
                      <TableCell>${Number(campaign.cost_per_lead || 0).toFixed(2)}</TableCell>
                      <TableCell>{campaign.drip_daily_limit || 0}</TableCell>
                      <TableCell>{campaign.delivered_count || 0}</TableCell>
                      <TableCell>{campaign.queued_count || 0}</TableCell>
                      <TableCell>${Number(campaign.subtotal_cost || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-slate-500">No business lead campaigns configured yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2"><Send className="w-4 h-4" /> Drip Feed Deliveries</CardTitle>
                <CardDescription>Delivered and queued business leads by campaign.</CardDescription>
              </div>
              <div className="w-72">
                <Select value={campaignId} onValueChange={(value) => { setCampaignId(value); loadData(value); }}>
                  <SelectTrigger data-testid="client-business-feed-campaign-filter">
                    <SelectValue placeholder="Filter campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Campaigns</SelectItem>
                    {overview.campaigns?.map((campaign) => (
                      <SelectItem key={campaign.campaign_id} value={campaign.campaign_id}>{campaign.campaign_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Input
                  data-testid="client-business-type-filter-input"
                  value={businessTypeInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBusinessTypeInput(val);
                    setBusinessTypeSuggestions(getBusinessTypeSuggestions(val).slice(0, 8));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyClientBusinessTypeToken(businessTypeInput);
                    }
                    if (e.key === 'Backspace' && !businessTypeInput.trim()) {
                      removeLastClientBusinessToken();
                    }
                  }}
                  placeholder="Type business type (ex: roofing), press Enter"
                />

                {businessTypeSuggestions.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border bg-white shadow" data-testid="client-business-type-suggestions">
                    {businessTypeSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.type}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm"
                        data-testid={`client-business-type-suggestion-${suggestion.type}`}
                        onClick={() => applyClientBusinessTypeToken(suggestion.type)}
                      >
                        {suggestion.type}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-2 flex flex-wrap gap-2" data-testid="client-business-type-token-container">
                  {businessTypeTokens.map((token) => (
                    <Badge key={token} variant="secondary">{token}</Badge>
                  ))}
                </div>
              </div>
              <Input data-testid="client-business-type-sic-codes" readOnly value={businessTypeCodeSnapshot.sic} placeholder="Auto SIC codes" />
              <Input data-testid="client-business-type-naics-codes" readOnly value={businessTypeCodeSnapshot.naics} placeholder="Auto NAICS codes" />
            </div>

            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Business Type</TableHead>
                    <TableHead>SIC/NAICS</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="whitespace-nowrap">Cost/Lead</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Delivered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFeed.length ? filteredFeed.map((row) => (
                    <TableRow key={row.id} data-testid={`client-business-feed-row-${row.id}`}>
                      <TableCell>{row.company_name || '-'}</TableCell>
                      <TableCell>{row.business_type || '-'}</TableCell>
                      <TableCell>{row.sic_code || '-'} / {row.naics_code || '-'}</TableCell>
                      <TableCell>{row.campaign_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={row.status === 'delivered' ? 'default' : 'outline'}>
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{row.source_provider || '-'}</TableCell>
                      <TableCell>${Number(row.cost_per_lead || 0).toFixed(2)}</TableCell>
                      <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>{row.delivered_at ? new Date(row.delivered_at).toLocaleString() : '-'}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-slate-500">No feed records yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
